import { useAuth } from "@elit-hair/elit-tools";
import { Button, Icon, Tabs, TabsList, TabsTrigger } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbCalendarEvent, TbCalendarWeek } from "react-icons/tb";
import { toast } from "sonner";
import z from "zod";

import { PageActions } from "@/components/page-actions";
import { AdminSurface } from "@/components/ui/admin-surface";
import { TeamMemberSelector } from "@/components/ui/team-member-selector";
import { TIME_FORMAT } from "@/constants/date-time";
import { SETTINGS_KEYS } from "@/constants/settings-keys";
import { ExceptionDatesEditor } from "@/features/schedule/components/exception-dates-editor";
import {
    DEFAULT_SCHEDULE_TIMEZONE_MODE,
    type ScheduleTimezoneMode,
} from "@/features/schedule/types/schedule-timezone.types";
import {
    getBrowserTimezone,
    projectScheduleFromStorage,
    projectScheduleToAgentTimezone,
    projectScheduleToDisplayTimezone,
    projectScheduleToStorage,
    resolveDisplayTimezone,
} from "@/features/schedule/utils/schedule-timezone.utils";
import {
    useCreateSchedule,
    useScheduleByUserId,
    useScheduleExceptionDates,
    useSyncScheduleExceptionDates,
    useUpdateSchedule,
} from "@/hooks/queries/schedule.queries";
import { getLocalSetting } from "@/providers/general/settings-provider";
import type { ExceptionDates, WeeklyWorkingHour } from "@/types/schedule.types";
import { APP_ROUTES_TO } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

import { SaveScheduleConfirmDialog } from "../components/save-schedule-confirm-dialog";
import {
    EditScheduleFlow,
    type ScheduleManageTab,
    type ScheduleViewMode,
    TimezoneModeToggle,
    TimezoneSelect,
    ViewModeToggle,
} from "../components/schedule-edit-views";

const DEFAULT_TIMEZONE = getBrowserTimezone();

// Exception dates expire after a year, so this window covers everything stored plus future planning.
const EXCEPTION_DATES_START = DateTime.now().minus({ years: 1 }).toISODate()!;
const EXCEPTION_DATES_END = DateTime.now().plus({ years: 2 }).toISODate()!;

function weeklyHoursKey(hours: WeeklyWorkingHour[]) {
    return JSON.stringify(
        hours.map(({ day, startTime, endTime }) => `${day} ${startTime}-${endTime}`).sort((a, b) => a.localeCompare(b))
    );
}

const ManageScheduleSearchSchema = z.object({
    userId: z.string().optional(),
    tab: z.enum(["weekly", "exceptions"]).optional().catch(undefined),
});

type ScheduleEditBaseline = {
    weeklyWorkingHours: WeeklyWorkingHour[];
    exceptionDates: ExceptionDates[];
    timezone: string;
};

export const Route = createFileRoute("/management/schedules/manage/")({
    component: ManageSchedulePage,
    validateSearch: searchValidator(ManageScheduleSearchSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "schedule_overview_manage",
                to: APP_ROUTES_TO.MANAGEMENT_SCHEDULES_MANAGE,
            },
            title: "schedule_overview_manage",
        },
    },
});

function ManageSchedulePage() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const { userId: selectedUserId, tab = "weekly" } = Route.useSearch({ structuralSharing: true });
    const { userData } = useAuth();
    const [viewMode, setViewMode] = useState<ScheduleViewMode>("grid");
    const [timezoneMode, setTimezoneMode] = useState<ScheduleTimezoneMode>(DEFAULT_SCHEDULE_TIMEZONE_MODE);
    const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);

    const [localTimezone, setLocalTimezone] = useState(DEFAULT_TIMEZONE);
    const [localWeeklyHours, setLocalWeeklyHours] = useState<WeeklyWorkingHour[]>([]);
    const [localExceptionDates, setLocalExceptionDates] = useState<ExceptionDates[]>([]);

    const {
        data: schedule,
        isLoading: scheduleLoading,
        isError: scheduleError,
        error: scheduleErr,
    } = useScheduleByUserId(selectedUserId);

    // Exception dates live in their own collection and are not part of the schedule response.
    const { data: exceptionDatesResponse, isLoading: exceptionDatesLoading } = useScheduleExceptionDates(
        EXCEPTION_DATES_START,
        EXCEPTION_DATES_END,
        { userIds: selectedUserId ? [selectedUserId] : undefined, enabled: Boolean(schedule) }
    );
    const storedExceptionDates = useMemo<ExceptionDates[]>(
        () => (exceptionDatesResponse ?? []).map(({ date, ranges, reason }) => ({ date, ranges, reason })),
        [exceptionDatesResponse]
    );
    const scheduleWithExceptions = useMemo(
        () => (schedule ? { ...schedule, exceptionDates: storedExceptionDates } : undefined),
        [schedule, storedExceptionDates]
    );
    const isScheduleDataLoading = scheduleLoading || (Boolean(schedule) && exceptionDatesLoading);

    const createSchedule = useCreateSchedule();
    const updateSchedule = useUpdateSchedule(selectedUserId ?? "");
    const syncExceptionDates = useSyncScheduleExceptionDates();

    const responseStatus =
        scheduleErr && typeof scheduleErr === "object" && "response" in scheduleErr
            ? (scheduleErr as { response?: { status?: number } }).response?.status
            : undefined;

    const isNotFound = scheduleError && responseStatus === 404;
    const isForbidden = scheduleError && responseStatus === 403;
    const displayFormat = getLocalSetting<string>(SETTINGS_KEYS.TIME_FORMAT) ?? TIME_FORMAT.TWENTY_FOUR_HOURS;

    const agentTimezone = localTimezone || DEFAULT_TIMEZONE;
    const displayTimezone = resolveDisplayTimezone(timezoneMode, agentTimezone);
    const canEditAgentTimezone = timezoneMode === "agent";
    const isMutating = createSchedule.isPending || updateSchedule.isPending;
    const showScheduleContent =
        Boolean(selectedUserId) && !isScheduleDataLoading && !isForbidden && (!scheduleError || isNotFound);

    const displayProjection = useMemo(
        () =>
            projectScheduleToDisplayTimezone(
                {
                    weeklyWorkingHours: localWeeklyHours,
                    exceptionDates: localExceptionDates,
                },
                agentTimezone,
                displayTimezone
            ),
        [localWeeklyHours, localExceptionDates, agentTimezone, displayTimezone]
    );

    const handleDisplayWeeklyHoursChange = useCallback(
        (hours: WeeklyWorkingHour[]) => {
            const projected = projectScheduleToAgentTimezone(
                hours,
                displayProjection.exceptionDates,
                displayTimezone,
                agentTimezone
            );
            setLocalWeeklyHours(projected.weeklyWorkingHours);
        },
        [displayProjection.exceptionDates, displayTimezone, agentTimezone]
    );

    // Exception dates save on their own (no edit mode) and always use the stored schedule timezone.
    const exceptionsTimezone = schedule?.timezone ?? agentTimezone;
    const exceptionsDisplayTimezone = resolveDisplayTimezone(timezoneMode, exceptionsTimezone);
    const displayExceptionDates = useMemo(() => {
        if (!scheduleWithExceptions) return [];
        const agentTimes = projectScheduleFromStorage(scheduleWithExceptions, exceptionsTimezone);
        return projectScheduleToDisplayTimezone(
            { exceptionDates: agentTimes.exceptionDates },
            exceptionsTimezone,
            exceptionsDisplayTimezone
        ).exceptionDates;
    }, [scheduleWithExceptions, exceptionsTimezone, exceptionsDisplayTimezone]);

    const handleSaveExceptionDates = useCallback(
        async (items: ExceptionDates[]) => {
            if (!schedule) return;
            const agentTimes = projectScheduleToAgentTimezone([], items, exceptionsDisplayTimezone, exceptionsTimezone);
            const storagePayload = projectScheduleToStorage([], agentTimes.exceptionDates, exceptionsTimezone);
            try {
                await syncExceptionDates.mutateAsync({
                    scheduleId: schedule.id,
                    previous: storedExceptionDates,
                    next: storagePayload.exceptionDates,
                });
                toast.success(t("exception_dates_saved"));
            } catch (error) {
                toast.error(t("exception_dates_save_failed"));
                throw error;
            }
        },
        [schedule, exceptionsDisplayTimezone, exceptionsTimezone, storedExceptionDates, syncExceptionDates, t]
    );

    // What is saved for the selected user, in agent-local time; the page is always editable against it.
    const baseline = useMemo<ScheduleEditBaseline | null>(() => {
        if (scheduleWithExceptions) {
            if (exceptionDatesLoading) return null;
            const agentTimes = projectScheduleFromStorage(scheduleWithExceptions, scheduleWithExceptions.timezone);
            return {
                timezone: scheduleWithExceptions.timezone,
                weeklyWorkingHours: agentTimes.weeklyWorkingHours,
                exceptionDates: agentTimes.exceptionDates,
            };
        }
        if (!scheduleLoading && selectedUserId) {
            return { timezone: DEFAULT_TIMEZONE, weeklyWorkingHours: [], exceptionDates: [] };
        }
        return null;
    }, [scheduleWithExceptions, exceptionDatesLoading, scheduleLoading, selectedUserId]);

    // Exception dates save on their own, so only the weekly plan and timezone count as unsaved changes.
    const isDirty = Boolean(
        baseline &&
            (localTimezone !== baseline.timezone ||
                weeklyHoursKey(localWeeklyHours) !== weeklyHoursKey(baseline.weeklyWorkingHours))
    );
    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;
    const loadedUserIdRef = useRef<string | undefined>(undefined);

    const resetToBaseline = useCallback(() => {
        if (!baseline) return;
        setLocalTimezone(baseline.timezone);
        setLocalWeeklyHours(baseline.weeklyWorkingHours);
    }, [baseline]);

    // Load the saved state when the user changes, and refresh it after saves or refetches unless there are edits.
    useEffect(() => {
        if (!baseline) return;
        setLocalExceptionDates(baseline.exceptionDates);
        const userChanged = loadedUserIdRef.current !== selectedUserId;
        if (userChanged || !isDirtyRef.current) resetToBaseline();
        loadedUserIdRef.current = selectedUserId;
    }, [baseline, selectedUserId, resetToBaseline]);

    // Warn before closing or reloading the tab with unsaved changes.
    useEffect(() => {
        if (!isDirty) return;
        const handleBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isDirty]);

    const handleSave = useCallback(async () => {
        if (!schedule) return;
        try {
            const storagePayload = projectScheduleToStorage(localWeeklyHours, localExceptionDates, localTimezone);
            await updateSchedule.mutateAsync({
                id: schedule.id,
                data: {
                    timezone: localTimezone,
                    weeklyWorkingHours: storagePayload.weeklyWorkingHours,
                },
            });
            toast.success(t("schedule_saved"));
            setSaveConfirmOpen(false);
        } catch {
            toast.error(t("schedule_save_failed"));
        }
    }, [schedule, localTimezone, localWeeklyHours, localExceptionDates, updateSchedule, t]);

    const handleCreate = useCallback(async () => {
        if (!selectedUserId) return;
        try {
            const storagePayload = projectScheduleToStorage(localWeeklyHours, localExceptionDates, localTimezone);
            await createSchedule.mutateAsync({
                userId: selectedUserId,
                timezone: localTimezone,
                weeklyWorkingHours: storagePayload.weeklyWorkingHours,
            });
            toast.success(t("schedule_created"));
        } catch {
            toast.error(t("schedule_create_failed"));
        }
    }, [selectedUserId, localTimezone, localWeeklyHours, localExceptionDates, createSchedule, t]);

    const handleMemberChange = useCallback(
        (memberId: string | null) => {
            if (isDirty && !window.confirm(t("schedule_unsaved_changes_confirm"))) return;
            navigate({
                to: APP_ROUTES_TO.MANAGEMENT_SCHEDULES_MANAGE,
                search: (prev) => ({ ...prev, userId: memberId ?? undefined }),
            });
        },
        [isDirty, navigate, t]
    );

    const renderPrimaryActions = () => {
        if (!showScheduleContent) return null;

        return (
            <>
                {isDirty ? (
                    <span className="rounded-md bg-amber-500/15 px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                        {t("schedule_unsaved_changes")}
                    </span>
                ) : null}
                {isDirty ? (
                    <Button type="button" variant="outline" size="sm" onClick={resetToBaseline} disabled={isMutating}>
                        {t("schedule_discard_changes")}
                    </Button>
                ) : null}
                <Button
                    type="button"
                    size="sm"
                    onClick={schedule ? () => setSaveConfirmOpen(true) : handleCreate}
                    disabled={isMutating || (Boolean(schedule) && !isDirty)}
                >
                    {schedule ? t("save") : t("create_schedule")}
                </Button>
            </>
        );
    };

    return (
        <div className="flex h-dvh min-h-0 flex-col gap-4 overflow-hidden">
            <div className="flex flex-wrap items-center gap-2">
                <div className="min-w-0 w-full sm:w-auto sm:min-w-56 sm:max-w-xs">
                    <TeamMemberSelector
                        selectedMemberId={selectedUserId}
                        onMemberChange={handleMemberChange}
                        currentUserId={userData?.id}
                        labels={{
                            resetSelection: "my_schedule",
                        }}
                    />
                </div>

                <div className="ml-auto flex flex-wrap items-center gap-2">
                    {canEditAgentTimezone ? (
                        <TimezoneSelect
                            value={agentTimezone}
                            onChange={setLocalTimezone}
                            disabled={isMutating}
                            className="w-full max-w-56 sm:w-56"
                        />
                    ) : (
                        <div className="flex h-9 max-w-56 items-center truncate rounded-md border border-border/60 bg-muted/20 px-3 text-sm font-medium">
                            {displayTimezone}
                        </div>
                    )}

                    <TimezoneModeToggle mode={timezoneMode} onChange={setTimezoneMode} />
                    {tab === "weekly" ? <ViewModeToggle viewMode={viewMode} onChange={setViewMode} /> : null}
                    <PageActions>{tab === "weekly" ? renderPrimaryActions() : null}</PageActions>
                </div>
            </div>

            {selectedUserId ? (
                <>
                    <Tabs
                        value={tab}
                        onValueChange={(value) =>
                            navigate({
                                search: (prev) => ({ ...prev, tab: value as ScheduleManageTab }),
                                replace: true,
                            })
                        }
                    >
                        <TabsList className="h-9">
                            <TabsTrigger value="weekly" className="gap-1.5 px-3">
                                <Icon icon={TbCalendarWeek} className="size-4" />
                                {t("schedule_tab_weekly")}
                            </TabsTrigger>
                            <TabsTrigger value="exceptions" className="gap-1.5 px-3">
                                <Icon icon={TbCalendarEvent} className="size-4" />
                                {t("exception_dates")}
                                {storedExceptionDates.length > 0 ? (
                                    <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground">
                                        {storedExceptionDates.length}
                                    </span>
                                ) : null}
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>

                    {isScheduleDataLoading && (
                        <div className="min-h-0 flex-1 animate-pulse rounded-2xl border border-border/60 bg-muted/40" />
                    )}

                    {tab === "weekly" && !isScheduleDataLoading && (schedule || isNotFound) && (
                        <EditScheduleFlow
                            weeklyHours={displayProjection.weeklyWorkingHours}
                            onWeeklyHoursChange={handleDisplayWeeklyHoursChange}
                            viewMode={viewMode}
                            isLoading={isMutating}
                            timezoneMode={timezoneMode}
                        />
                    )}

                    {tab === "exceptions" &&
                        !isScheduleDataLoading &&
                        !isForbidden &&
                        (!scheduleError || isNotFound) && (
                            <div className="min-h-0 flex-1 overflow-hidden">
                                {schedule ? null : (
                                    <p className="mb-3 rounded-xl border border-dashed border-border/70 px-4 py-3 text-sm text-muted-foreground">
                                        {t("exception_requires_schedule")}
                                    </p>
                                )}
                                <ExceptionDatesEditor
                                    value={displayExceptionDates}
                                    onSave={handleSaveExceptionDates}
                                    canEdit={Boolean(schedule)}
                                />
                            </div>
                        )}

                    {saveConfirmOpen && schedule && (
                        <SaveScheduleConfirmDialog
                            open={saveConfirmOpen}
                            onOpenChange={setSaveConfirmOpen}
                            onConfirm={handleSave}
                            isSaving={updateSchedule.isPending}
                            oldWeekly={baseline?.weeklyWorkingHours ?? localWeeklyHours}
                            newWeekly={localWeeklyHours}
                            oldExceptions={baseline?.exceptionDates ?? localExceptionDates}
                            newExceptions={localExceptionDates}
                            oldTimezone={baseline?.timezone ?? localTimezone}
                            newTimezone={localTimezone}
                            displayFormat={displayFormat}
                        />
                    )}

                    {!isScheduleDataLoading && isForbidden && (
                        <AdminSurface className="p-6">
                            <p className="text-sm text-muted-foreground">{t("schedule_no_permission")}</p>
                        </AdminSurface>
                    )}

                    {!isScheduleDataLoading && scheduleError && !isNotFound && !isForbidden && (
                        <AdminSurface className="p-6">
                            <p className="text-sm text-destructive">{t("error_loading_schedule")}</p>
                        </AdminSurface>
                    )}
                </>
            ) : (
                <AdminSurface className="flex min-h-0 flex-1 items-center justify-center p-8">
                    <p className="text-center text-sm text-muted-foreground">{t("select_user_to_manage_schedule")}</p>
                </AdminSurface>
            )}
        </div>
    );
}
